import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Sparkles,
  BookOpen,
  Heart,
  Send,
  CheckCircle2,
  GraduationCap,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { API } from "@/lib/auth";
import { IMG } from "@/lib/data";

const PILLAR_ICONS = [BookOpen, Heart, Sparkles];

const DEFAULTS = {
  eyebrow: "NøoksPro · Free Education",
  title_line_1: "Free education,",
  title_line_2: "on me.",
  subhead:
    "A growing library of free tips, tutorials and honest behind-the-chair moments from Malek — made to help you become a more confident, calmer, more skilled hair extension stylist.",
  offer_line:
    "Sign up below and, as a little welcome-in, I'll send you a 100% off code for my 16+ page Hair Extension Aftercare Guide magazine (normally £6.99) — yours to keep, on the house.",
  hero_card_title: "A community for stylists who want to grow — beautifully.",
  hero_card_tag: "Free forever",
  cta_form_button: "Join & get my free guide",
  consent_note:
    "By joining you agree to receive free education emails from Malek Dridi Hair & Beauty. Unsubscribe anytime — no hard feelings.",
  pillars_heading_1: "Small lessons.",
  pillars_heading_2: "Big shifts.",
  pillars: [
    {
      title: "Real, no-fluff lessons",
      body:
        "Behind-the-chair tips, mini tutorials and cheatsheets — the stuff I wish someone had shown me starting out.",
    },
    {
      title: "Made with love, not sales",
      body:
        "Free forever. This is my way of giving back to the industry that raised me — and lifting the next generation up.",
    },
    {
      title: "Early access to drops",
      body:
        "Be first to hear about new NøoksPro Range, courses, price-friendly bundles and members-only offers.",
    },
  ],
  gift_eyebrow: "Your welcome gift",
  gift_title_1: "The Hair Extension Aftercare Guide magazine —",
  gift_title_2: "on me.",
  gift_body:
    "A 16+ page magazine-style aftercare guide — consultations, colour matching, application, aftercare and troubleshooting — the exact framework I use in the salon. Yours for £0 when you join NøoksPro Free Education.",
  gift_price_original: "£6.99",
  gift_price_now: "£0",
  gift_cta_primary: "Get my free code",
  gift_cta_secondary: "Explore paid courses",
  success_title: "You're in.",
  success_body:
    "Check your inbox — your free download code for the Hair Extension Aftercare Guide magazine is on its way. (Peek in spam just in case — then drag me to your primary!)",
  success_button: "Claim your free guide",
};

export default function FreeEducation() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [content, setContent] = useState(DEFAULTS);
  const [productImage, setProductImage] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/site/content`)
      .then((r) => {
        const fe = r.data?.free_education;
        if (fe && typeof fe === "object") {
          setContent({ ...DEFAULTS, ...fe });
        }
      })
      .catch(() => {});
    // Pull the actual Aftercare Guide product image so the promo stays in
    // sync with whatever image is set on the Shop product.
    axios
      .get(`${API}/shop/products/complete-hair-extension-guide`)
      .then((r) => {
        const p = r.data || {};
        const img = p.image || (Array.isArray(p.images) && p.images[0]) || "";
        if (img) setProductImage(img);
      })
      .catch(() => {});
  }, []);

  const c = content;
  // Precedence: admin CMS override > actual product image > local fallback
  const heroImage = c.hero_image || productImage || IMG.classroom;
  const giftImage = c.gift_image || productImage || IMG.stylingProcess;
  const pillars = (c.pillars && c.pillars.length ? c.pillars : DEFAULTS.pillars).slice(0, 6);

  const submit = async (e) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email,
        name: form.name.trim() || undefined,
        source: "free_education",
      });
      setDone(true);
      if (res.data?.already_subscribed) {
        toast.success("You're already in — check your inbox for your code.");
      } else {
        toast.success("Welcome in — check your inbox for your free guide.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="page-free-education"
      className="relative pt-32 pb-24 overflow-hidden"
    >
      {/* Soft blush gradient wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(60% 45% at 15% 5%, hsl(var(--blush)/0.65) 0%, transparent 60%), radial-gradient(45% 40% at 100% 20%, hsl(var(--blush)/0.55) 0%, transparent 55%), linear-gradient(180deg, hsl(var(--cream)) 0%, #fff 100%)",
        }}
      />

      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        {/* Hero */}
        <div className="grid gap-14 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="overline text-[hsl(var(--gold))]">{c.eyebrow}</div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4 tracking-tight text-balance">
                {c.title_line_1}{" "}
                <span className="italic font-light">{c.title_line_2}</span>
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-lg text-[hsl(var(--onyx)/0.72)] max-w-xl leading-relaxed">
                {c.subhead}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-4 text-[hsl(var(--onyx)/0.6)] max-w-xl leading-relaxed">
                {c.offer_line}
              </p>
            </Reveal>

            {/* Form / Success card */}
            <div className="mt-10 max-w-lg">
              {!done ? (
                <form
                  data-testid="free-education-form"
                  onSubmit={submit}
                  className="rounded-[2rem] bg-white/85 backdrop-blur border border-white/70 p-6 sm:p-8 shadow-[0_20px_60px_-30px_hsl(var(--onyx)/0.35)]"
                >
                  <div className="grid gap-5">
                    <div>
                      <Label htmlFor="fe-name">Name</Label>
                      <Input
                        id="fe-name"
                        data-testid="free-education-name"
                        className="mt-2 h-12 rounded-2xl bg-white"
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                        placeholder="Your first name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="fe-email">Email</Label>
                      <Input
                        id="fe-email"
                        type="email"
                        required
                        data-testid="free-education-email"
                        className="mt-2 h-12 rounded-2xl bg-white"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                        placeholder="you@email.com"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="free-education-submit"
                      className="btn-pill btn-primary self-start disabled:opacity-70"
                    >
                      {loading ? (
                        "Sending your code..."
                      ) : (
                        <>
                          {c.cta_form_button} <Send size={16} />
                        </>
                      )}
                    </button>
                    <p className="text-[11px] text-[hsl(var(--onyx)/0.5)] leading-relaxed">
                      {c.consent_note}
                    </p>
                  </div>
                </form>
              ) : (
                <div
                  data-testid="free-education-success"
                  className="rounded-[2rem] bg-white/95 backdrop-blur border border-[hsl(var(--gold)/0.35)] p-8 shadow-[0_20px_60px_-30px_hsl(var(--onyx)/0.35)]"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-[hsl(var(--blush)/0.6)] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2
                        size={22}
                        className="text-[hsl(var(--onyx))]"
                      />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl">{c.success_title}</h3>
                      <p className="mt-2 text-[hsl(var(--onyx)/0.7)] leading-relaxed text-sm">
                        {c.success_body}
                      </p>
                      <Link
                        to="/shop/complete-hair-extension-guide"
                        data-testid="free-education-goto-product"
                        className="btn-pill btn-primary mt-5"
                      >
                        {c.success_button} <ArrowUpRight size={16} />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative">
                <div className="rounded-[2.5rem] overflow-hidden aspect-[4/5] hover-zoom shadow-[0_40px_80px_-40px_hsl(var(--onyx)/0.45)]">
                  <img
                    src={heroImage}
                    alt="NøoksPro Free Education"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 sm:-left-10 rounded-3xl bg-white p-5 sm:p-6 border border-white/80 shadow-2xl max-w-[260px]">
                  <div className="flex items-center gap-2 text-[hsl(var(--gold))]">
                    <GraduationCap size={18} />
                    <span className="overline">{c.hero_card_tag}</span>
                  </div>
                  <p className="mt-2 font-display text-lg leading-snug">
                    {c.hero_card_title}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Pillars */}
        <div className="mt-28">
          <Reveal>
            <div className="max-w-2xl">
              <div className="overline">What you'll get</div>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 tracking-tight">
                {c.pillars_heading_1}{" "}
                <span className="italic font-light">{c.pillars_heading_2}</span>
              </h2>
            </div>
          </Reveal>

          <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
            {pillars.map((p, idx) => {
              const Icon = PILLAR_ICONS[idx % PILLAR_ICONS.length];
              return (
                <Item key={`${p.title}-${idx}`}>
                  <div className="rounded-[2rem] bg-white/90 backdrop-blur border border-[hsl(var(--onyx)/0.06)] p-7 h-full transition-transform duration-500 hover:-translate-y-1">
                    <div className="w-11 h-11 rounded-full bg-[hsl(var(--blush)/0.65)] flex items-center justify-center">
                      <Icon size={18} className="text-[hsl(var(--onyx))]" />
                    </div>
                    <h3 className="font-display text-2xl mt-5">{p.title}</h3>
                    <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.65)] leading-relaxed">
                      {p.body}
                    </p>
                  </div>
                </Item>
              );
            })}
          </Stagger>
        </div>

        {/* Gift block */}
        <div className="mt-28">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] p-10 sm:p-16">
              <div
                aria-hidden
                className="absolute inset-0 opacity-30"
                style={{
                  background:
                    "radial-gradient(60% 60% at 90% 10%, hsl(var(--blush)/0.35), transparent 60%), radial-gradient(50% 50% at 10% 100%, hsl(var(--gold)/0.25), transparent 60%)",
                }}
              />
              <div className="relative grid gap-10 md:grid-cols-2 items-center">
                <div>
                  <div className="overline text-[hsl(var(--gold))]">
                    {c.gift_eyebrow}
                  </div>
                  <h3 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight">
                    {c.gift_title_1}{" "}
                    <span className="italic font-light">{c.gift_title_2}</span>
                  </h3>
                  <p className="mt-5 text-[hsl(var(--cream)/0.7)] leading-relaxed max-w-lg">
                    {c.gift_body}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <a
                      href="#top"
                      onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      data-testid="free-education-scroll-form"
                      className="btn-pill btn-primary"
                    >
                      {c.gift_cta_primary} <ArrowUpRight size={16} />
                    </a>
                    <Link
                      to="/academy"
                      data-testid="free-education-explore-academy"
                      className="btn-pill btn-outline text-[hsl(var(--cream))] border-[hsl(var(--cream)/0.35)] hover:bg-white/10"
                    >
                      {c.gift_cta_secondary}
                    </Link>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-[4/3] rounded-[2rem] overflow-hidden">
                    <img
                      src={giftImage}
                      alt="Hair Extension Aftercare Guide magazine"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-5 -right-5 rounded-2xl bg-[hsl(var(--cream))] text-[hsl(var(--onyx))] px-5 py-3 shadow-2xl">
                    <div className="overline text-[hsl(var(--gold))]">
                      Normally
                    </div>
                    <div className="font-display text-xl">
                      <span className="line-through opacity-60">
                        {c.gift_price_original}
                      </span>{" "}
                      <span className="italic">{c.gift_price_now}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
